export type TVector3 = {
  x: number;
  y: number;
  z: number;
};

type TQuaternion = {
  x: number;
  y: number;
  z: number;
  w: number;
};

type TEuler = {
  x: number;
  y: number;
  z: number;
  order: string;
};

function quaternionFromEuler(euler: TEuler) {
  const q: TQuaternion = {
    x: 0,
    y: 0,
    z: 0,
    w: 1,
  };

  const x = euler.x,
    y = euler.y,
    z = euler.z,
    order = euler.order;

  // http://www.mathworks.com/matlabcentral/fileexchange/
  // 	20696-function-to-convert-between-dcm-euler-angles-quaternions-and-euler-vectors/
  //	content/SpinCalc.m

  const cos = Math.cos;
  const sin = Math.sin;

  const c1 = cos(x / 2);
  const c2 = cos(y / 2);
  const c3 = cos(z / 2);

  const s1 = sin(x / 2);
  const s2 = sin(y / 2);
  const s3 = sin(z / 2);

  switch (order) {
    case "XYZ":
      q.x = s1 * c2 * c3 + c1 * s2 * s3;
      q.y = c1 * s2 * c3 - s1 * c2 * s3;
      q.z = c1 * c2 * s3 + s1 * s2 * c3;
      q.w = c1 * c2 * c3 - s1 * s2 * s3;
      break;

    case "YXZ":
      q.x = s1 * c2 * c3 + c1 * s2 * s3;
      q.y = c1 * s2 * c3 - s1 * c2 * s3;
      q.z = c1 * c2 * s3 - s1 * s2 * c3;
      q.w = c1 * c2 * c3 + s1 * s2 * s3;
      break;

    case "ZXY":
      q.x = s1 * c2 * c3 - c1 * s2 * s3;
      q.y = c1 * s2 * c3 + s1 * c2 * s3;
      q.z = c1 * c2 * s3 + s1 * s2 * c3;
      q.w = c1 * c2 * c3 - s1 * s2 * s3;
      break;

    case "ZYX":
      q.x = s1 * c2 * c3 - c1 * s2 * s3;
      q.y = c1 * s2 * c3 + s1 * c2 * s3;
      q.z = c1 * c2 * s3 - s1 * s2 * c3;
      q.w = c1 * c2 * c3 + s1 * s2 * s3;
      break;

    case "YZX":
      q.x = s1 * c2 * c3 + c1 * s2 * s3;
      q.y = c1 * s2 * c3 + s1 * c2 * s3;
      q.z = c1 * c2 * s3 - s1 * s2 * c3;
      q.w = c1 * c2 * c3 - s1 * s2 * s3;
      break;

    case "XZY":
      q.x = s1 * c2 * c3 - c1 * s2 * s3;
      q.y = c1 * s2 * c3 - s1 * c2 * s3;
      q.z = c1 * c2 * s3 + s1 * s2 * c3;
      q.w = c1 * c2 * c3 + s1 * s2 * s3;
      break;

    default:
      console.warn(
        "THREE.Quaternion: .setFromEuler() encountered an unknown order: " +
          order
      );
  }

  return q;
}

function applyQuaternion(vector: TVector3, q: TQuaternion) {
  let x = vector.x,
    y = vector.y,
    z = vector.z;

  const qx = q.x,
    qy = q.y,
    qz = q.z,
    qw = q.w;

  // calculate quat * vector

  const ix = qw * x + qy * z - qz * y;
  const iy = qw * y + qz * x - qx * z;
  const iz = qw * z + qx * y - qy * x;
  const iw = -qx * x - qy * y - qz * z;

  // calculate result * inverse quat

  x = ix * qw + iw * -qx + iy * -qz - iz * -qy;
  y = iy * qw + iw * -qy + iz * -qx - ix * -qz;
  z = iz * qw + iw * -qz + ix * -qy - iy * -qx;

  return {
    x,
    y,
    z,
  };
}

class VectorUtils {
  static applyEuler(
    vector: TVector3,
    euler: { alpha: number; beta: number; gamma: number }
  ) {
    return applyQuaternion(
      vector,
      quaternionFromEuler({
        x: euler.alpha,
        y: euler.beta,
        z: euler.gamma,
        order: "XYZ",
      })
    );
  }

  static distanceToSquared(vectorA: TVector3, vectorB: TVector3) {
    const dx = vectorA.x - vectorB.x,
      dy = vectorA.y - vectorB.y,
      dz = vectorA.z - vectorB.z;
    return dx * dx + dy * dy + dz * dz;
  }

  static distanceToOrigin(vector: TVector3) {
    return Math.sqrt(
      VectorUtils.distanceToSquared(vector, { x: 0, y: 0, z: 0 })
    );
  }
}

(window as any).distanceToOrigin = VectorUtils.distanceToOrigin;

export default VectorUtils;
